import { useState, useEffect, useCallback } from "react";
import { AlertTriangle, RefreshCw, X, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type BannerSeverity = "error" | "warning" | "info";

interface ErrorBannerProps {
  message: string;
  severity?: BannerSeverity;
  onRetry?: () => void;
  onDismiss?: () => void;
  autoRetry?: boolean;
  autoRetryInterval?: number;
}

export function ErrorBanner({
  message,
  severity = "error",
  onRetry,
  onDismiss,
  autoRetry = false,
  autoRetryInterval = 5000,
}: ErrorBannerProps) {
  const [retrying, setRetrying] = useState(false);

  const handleRetry = useCallback(async () => {
    setRetrying(true);
    try {
      await onRetry?.();
    } finally {
      setRetrying(false);
    }
  }, [onRetry]);

  useEffect(() => {
    if (!autoRetry || !onRetry) return;
    const id = setInterval(handleRetry, autoRetryInterval);
    return () => clearInterval(id);
  }, [autoRetry, autoRetryInterval, handleRetry, onRetry]);

  const Icon = severity === "error" ? AlertTriangle : severity === "warning" ? WifiOff : AlertTriangle;

  return (
    <div
      role="alert"
      className={cn(
        "animate-banner-slide-down flex items-center gap-3 px-4 py-2.5 text-sm",
        severity === "error" && "bg-destructive/10 text-destructive",
        severity === "warning" && "bg-yellow-500/10 text-yellow-700 dark:text-yellow-400",
        severity === "info" && "bg-blue-500/10 text-blue-700 dark:text-blue-400"
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="flex-1">{message}</span>
      {onRetry && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 px-2"
          onClick={handleRetry}
          disabled={retrying}
        >
          <RefreshCw className={cn("h-3.5 w-3.5", retrying && "animate-spin")} />
          Retry
        </Button>
      )}
      {onDismiss && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}
